import { Link, useNavigate } from 'react-router-dom';
import { IconArrowRight, IconCompass, IconMail } from '../components/icons';
import { Empty, HealthRing, Skeleton, StatusPill, TideChart, useAsync } from '../components/ui';
import { Alert, api, canSetUpWorkspace, EmailListItem, Stats } from '../lib/api';
import { IconBell } from '../components/icons';
import { ago, ms, num, pct } from '../lib/format';
import { useSession } from '../lib/session';
import { t } from '../lib/i18n';

const STEP_LABEL: Record<string, [string, string]> = {
  profile: ['Профиль компании', '/onboarding'],
  domain: ['Подтвердить домен', '/domains'],
  api_key: ['Создать API-ключ', '/api-keys'],
  test_email: ['Отправить первое письмо', '/onboarding'],
  webhook: ['Подключить вебхук', '/webhooks'],
};

export function OverviewPage() {
  const { me } = useSession();
  const nav = useNavigate();
  const stats = useAsync(() => api.get<Stats>('/stats?days=14'));
  const recent = useAsync(() => api.get<{ items: EmailListItem[] }>('/emails?limit=6'));
  const alerts = useAsync(() => api.get<{ items: Alert[] }>('/alerts'));
  const s = stats.data;
  const hour = new Date().getHours();
  const hello = hour < 6 ? t('Тихой ночи') : hour < 12 ? t('Доброе утро') : hour < 18 ? t('Добрый день') : t('Добрый вечер');
  const open = (me && canSetUpWorkspace(me) ? me.workspace.onboarding.steps : []).filter((x) => !me?.workspace.onboarding.completed.includes(x));

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <h1>{hello}, {me?.user.name.split(' ')[0]}</h1>
          <p>{t('Последние 14 дней в океане {name}.', { name: me?.workspace.name ?? '' })}</p>
        </div>
        <Link to="/emails" className="btn">{t('Все письма')} <IconArrowRight size={15} /></Link>
      </div>

      {!!alerts.data?.items.length && (
        <div className="alerts" role="list" aria-label={t('Оповещения')}>
          {alerts.data.items.map((a) => (
            <Link key={`${a.kind}-${a.link}`} to={a.link} className={`alert ${a.severity}`} role="listitem" data-track="alert.open" data-track-id={a.kind}>
              <IconBell size={18} color={a.severity === 'critical' ? 'var(--coral)' : a.severity === 'warning' ? 'var(--sun)' : 'var(--accent)'} />
              <div>
                <b>{a.title}</b>
                <p>{t(a.body)}</p>
              </div>
            </Link>
          ))}
        </div>
      )}

      {open.length > 0 && (
        <div className="card card-pad" style={{ marginBottom: 16, background: 'linear-gradient(120deg, rgba(26,127,224,.12), rgba(23,195,207,.08))' }}>
          <div className="row wrap">
            <IconCompass size={22} color="var(--accent)" />
            <div className="grow" style={{ flex: 1, minWidth: 220 }}>
              <h3>{t('Погружение не завершено')}</h3>
              <p className="muted small">{t('Осталось шагов: {n}. Каждый из них приближает письма к «Входящим».', { n: open.length })}</p>
            </div>
            <div className="chips">
              {open.map((x) => (
                <button key={x} className="chip" onClick={() => nav(STEP_LABEL[x][1])}>{t(STEP_LABEL[x][0])}</button>
              ))}
            </div>
            <Link to="/onboarding" className="btn btn-primary btn-sm">{t('Продолжить')}</Link>
          </div>
        </div>
      )}

      <div className="grid grid-kpi">
        {!s
          ? Array.from({ length: 5 }, (_, i) => <div key={i} className="card kpi"><Skeleton h={14} w={90} /><div style={{ height: 10 }} /><Skeleton h={30} w={120} /></div>)
          : [
              [t('Писем'), num(s.totals.all), t('за 14 дней')],
              [t('Принято провайдером'), pct(s.rates.delivered), t('от переданных')],
              [t('Отскоки'), pct(s.rates.bounce, 2), t('норма < 2%')],
              [t('Жалобы'), pct(s.rates.complaint, 3), t('норма < 0,1%')],
              [t('Задержка p95'), ms(s.latency_ms.p95), t('медиана {v}', { v: ms(s.latency_ms.p50) })],
            ].map(([label, value, sub]) => (
              <div key={label} className="card kpi">
                <div className="label">{label}</div>
                <div className="value">{value}</div>
                <div className="sub">{sub}</div>
              </div>
            ))}
      </div>

      <div className="grid grid-2" style={{ marginTop: 16 }}>
        <div className="card">
          <div className="card-head">
            <h3>{t('Прилив писем')}</h3>
            <span className="row small muted"><span style={{ width: 10, height: 3, background: 'var(--accent)', borderRadius: 2 }} /> {t('все')} <span style={{ width: 10, height: 3, background: '#f2605a', borderRadius: 2, marginLeft: 8 }} /> {t('проблемы')}</span>
          </div>
          <div className="card-pad">{s ? <TideChart series={s.series} /> : <Skeleton h={200} />}</div>
        </div>
        <div className="card">
          <div className="card-head"><h3>{t('Состояние моря')}</h3></div>
          <div className="card-pad stack" style={{ alignItems: 'center' }}>
            {s ? <HealthRing value={s.health} /> : <Skeleton h={150} w={150} />}
            <div className="stack small" style={{ width: '100%', gap: 6 }}>
              <div className="row"><span className="muted">{t('Подтверждённые домены')}</span><span className="spacer" /><b>{s ? `${s.domains.verified} / ${s.domains.total}` : '—'}</b></div>
              <div className="row"><span className="muted">{t('Отскоки')}</span><span className="spacer" /><b>{s ? pct(s.rates.bounce, 2) : '—'}</b></div>
              <div className="row"><span className="muted">{t('Жалобы')}</span><span className="spacer" /><b>{s ? pct(s.rates.complaint, 3) : '—'}</b></div>
            </div>
            <p className="small muted">{t('Здоровье падает от отскоков, жалоб, ошибок и отсутствия подтверждённого домена — тех же сигналов, по которым почтовики решают, куда класть письма.')}</p>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-head"><h3>{t('Последние письма')}</h3><Link to="/emails" className="small">{t('Все')} →</Link></div>
        {recent.loading ? (
          <div className="card-pad stack"><Skeleton /><Skeleton /><Skeleton /></div>
        ) : !recent.data?.items.length ? (
          <Empty icon={<IconMail size={36} />} title={t('Океан пока спокоен')} action={<Link className="btn btn-primary" to="/api-keys">{t('Получить API-ключ')}</Link>}>
            {t('Отправьте первое письмо через API — оно появится здесь через мгновение.')}
          </Empty>
        ) : (
          <table className="table">
            <tbody>
              {recent.data.items.map((m) => (
                <tr key={m.id} className="click" onClick={() => nav(`/emails/${m.id}`)}>
                  <td style={{ width: 130 }}><StatusPill status={m.status} /></td>
                  <td className="truncate"><strong>{m.subject}</strong></td>
                  <td className="truncate muted">{m.to.join(', ')}</td>
                  <td className="muted small" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{ago(m.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
